import clone from 'lodash.clonedeep';
import moment from 'moment';

import {
  toNumber,
  toCurrency,
  toPercentage,
  toDate,
  getSortedDates,
  getDateFromString,
} from './format.util';

import { DEFAULT_COLLECTION_DATA } from '../constants';

function calculateCollectionCardProfit(card, quantity, currentValue) {
  let profit = 0;

  if (typeof quantity === 'number' && typeof card.investment === 'number') {
    if (card.sold && typeof card.priceSold === 'number') {
      profit = (card.priceSold * quantity) - (card.investment * quantity);
    } else if (typeof currentValue === 'number') {
      profit = (currentValue * quantity) - (card.investment * quantity);
    }
  }

  if (card.ownsPercentage && typeof card.ownershipPercentage === 'number' && card.ownershipPercentage > 0 && card.ownershipPercentage < 100) {
    profit = profit * (card.ownershipPercentage / 100);
  }

  return profit;
}

export function getCollectionCardInvestments(card) {
  let quantity = 1;
  let quantitySold = 0;
  let profit = 0;
  let totalInvestment = 0;
  let collectionInvestment = 0;
  let totalSoldValue = 0;
  let quantityAvailable = 0;
  let newestPurchase = { price: 0, date: null };

  if (card) {
    if (card.willManageQuantity && card.individualCards && card.individualCards.length > 0) {
      card.individualCards.forEach((item) => {
        profit += calculateCollectionCardProfit(item, 1, card.currentValue);
        totalInvestment += item.investment || 0;

        let itemInvestment = item.investment || 0;

        if (item.ownsPercentage && typeof item.ownershipPercentage === 'number' && item.ownershipPercentage > 0 && item.ownershipPercentage < 100) {
          itemInvestment *= (item.ownershipPercentage / 100);
        }

        collectionInvestment += itemInvestment;

        if (item.sold) {
          totalSoldValue += item.priceSold || 0;
          quantitySold += 1;
        } else {
          quantityAvailable += 1;
        }

        if (item.datePurchased && (!newestPurchase.date || item.datePurchased > newestPurchase.date)) {
          newestPurchase = { price: item.investment, date: item.datePurchased };
        }
      });

      quantity = card.individualCards.length;
    } else {
      profit = calculateCollectionCardProfit(card, card.quantity, card.currentValue);
      quantity = card.quantity || 1;
      totalInvestment = (card.investment || 0) * quantity;

      let itemInvestment = (card.investment || 0) * quantity;

      if (card.ownsPercentage && typeof card.ownershipPercentage === 'number' && card.ownershipPercentage > 0 && card.ownershipPercentage < 100) {
        itemInvestment *= (card.ownershipPercentage / 100);
      }

      collectionInvestment = itemInvestment;

      if (card.sold) {
        totalSoldValue += (card.priceSold || 0) * quantity;
        quantitySold += quantity;
      } else {
        quantityAvailable += quantity;
      }

      newestPurchase = { price: card.investment, date: card.datePurchased };
    }
  }

  return {
    quantity,
    quantitySold,
    quantityAvailable,
    hasQuantityAvailable: quantityAvailable > 0,
    investment: quantity > 0 ? totalInvestment / quantity : 0,
    priceSold: quantitySold > 0 ? totalSoldValue / quantitySold : 0,
    profit,
    sold: quantitySold > 0,
    newestPurchase,
    totalInvestment: collectionInvestment,
  };
}

export async function getEstimatedValueFromMultipliers(multipliers, cardService) {
  if (!multipliers) {
    return { filteredMultipliers: [], estimatedValue: null };
  }

  let filteredMultipliers = multipliers
    .filter(({
      card,
      value,
      cardLabel,
    }) => Boolean(card && value && cardLabel) && typeof value === 'number');

  if (filteredMultipliers.length === 0) {
    return { filteredMultipliers: [], estimatedValue: null };
  }

  let estimatedValue = null;

  for (let i = 0; i < filteredMultipliers.length; i += 1) {
    if (!filteredMultipliers[i].currentValue) {
      const cardWithValue = await cardService.getCard(filteredMultipliers[i].card);

      if (cardWithValue && cardWithValue.stats && typeof cardWithValue.stats.currentValue === 'number') {
        filteredMultipliers[i].currentValue = cardWithValue.stats.currentValue;
      }
    }
  }

  filteredMultipliers = filteredMultipliers.filter(({ currentValue }) => typeof currentValue === 'number');

  let totalPredictedCost = 0;

  filteredMultipliers.forEach(({ currentValue, value }) => {
    totalPredictedCost += currentValue * value;
  });

  if (totalPredictedCost > 0 && filteredMultipliers && filteredMultipliers.length > 0) {
    estimatedValue = totalPredictedCost / filteredMultipliers.length;
  }

  return { filteredMultipliers, estimatedValue };
}

export function getGuideValue(index, saleDate, salePrice) {
  const guideValue = { value: null, ratio: null, confidence: 1, indexPercentChange: 0 };
  const TWO_MONTHS_IN_MS = 60 * 60 * 1000 * 24 * 60;
  const indexObject = Object.keys(index && index.dailyIndexTotal ? index.dailyIndexTotal : {})
    .sort((a, b) => getDateFromString(b) - getDateFromString(a))
    .map((dateString) => ({
      p: index.dailyIndexTotal[dateString],
      d: moment(getDateFromString(dateString)).add(10, 'hours').toDate(),
    }));

  let saleDateIndex;

  if (indexObject && indexObject.length > 0) {
    if (saleDate < indexObject[indexObject.length - 1].d) {
      guideValue.ratio = salePrice / indexObject[indexObject.length - 1].p;
    } else {
      const temp = indexObject.map((index) => Math.abs(saleDate.getTime() - index.d.getTime()));

      saleDateIndex = temp.indexOf(Math.min(...temp));

      if (saleDateIndex > -1 && Math.abs(saleDate.getTime() - indexObject[saleDateIndex].d.getTime()) < TWO_MONTHS_IN_MS) {
        guideValue.ratio = salePrice / indexObject[saleDateIndex].p;
      }
    }

    if (guideValue.ratio) {
      const currentIndexValue = indexObject[0].p;

      guideValue.value = currentIndexValue * guideValue.ratio;

      if (indexObject[saleDateIndex] && indexObject[saleDateIndex].p !== 0) {
        guideValue.indexPercentChange = Number(Number((currentIndexValue - indexObject[saleDateIndex].p) / indexObject[saleDateIndex].p).toFixed(4));
      }
    }
  }

  if (moment(saleDate) > moment().subtract(2, 'weeks')) {
    guideValue.confidence = 5;
  } else if (moment(saleDate) < moment().subtract(2, 'weeks') && moment(saleDate) > moment().subtract(1, 'months')) {
    guideValue.confidence = 4;
  } else if (moment(saleDate) < moment().subtract(1, 'months') && moment(saleDate) > moment().subtract(3, 'months')) {
    guideValue.confidence = 3;
  } else if (moment(saleDate) < moment().subtract(3, 'months') && moment(saleDate) > moment().subtract(6, 'months')) {
    guideValue.confidence = 2;
  }

  let description = '';

  if (guideValue && guideValue.indexPercentChange && guideValue.value) {
    description = `The CL Value is calculated by looking at the last sale of this card on ${toDate(saleDate)} for ${toCurrency(salePrice)}. `;
    description += `This card matches the ${index.label} index which has gone ${guideValue.indexPercentChange < 0 ? 'down' : 'up'} by ${toPercentage(guideValue.indexPercentChange)} since the sale date. `;
    description += 'If we multiply the sale price by this percentage we arrive at our value.';
  }

  guideValue.description = description;

  return guideValue;
}

export function getGuideValueFromCollectionCard(index, collectionCard) {
  if (!index) {
    return { value: null, ratio: null, id: '' };
  }

  const collectionCardInvestments = getCollectionCardInvestments(collectionCard);

  let indexDate = collectionCardInvestments.newestPurchase.date;
  let indexPrice = collectionCardInvestments.newestPurchase.price;

  if (collectionCard.useSalesForValue && collectionCard.stats && collectionCard.stats.newestSale) {
    indexDate = collectionCard.stats.newestSale.date;
    indexPrice = collectionCard.stats.newestSale.price;
  } else if (collectionCard.playerIndexPrice && collectionCard.playerIndexDate) {
    indexDate = collectionCard.playerIndexDate;
    indexPrice = collectionCard.playerIndexPrice;
  }

  return getGuideValue(index, indexDate, indexPrice);
}

export async function getCollectionCardToSave(collectionCard, cardService, indexService, firebase, collectionId, uid) {
  const collectionCardToSave = clone(collectionCard);

  collectionCardToSave.collectionId = collectionId;
  collectionCardToSave.uid = uid;

  let multipliers = [];
  let failedToSetupValues = false;

  if (!collectionCard.cardId) {
    if (!collectionCardToSave.stats) {
      collectionCardToSave.stats = {};
    }

    if (Array.isArray(collectionCardToSave.multipliers) && collectionCardToSave.multipliers.length > 0) {
      const { filteredMultipliers, estimatedValue } = await getEstimatedValueFromMultipliers(collectionCardToSave.multipliers, cardService);

      multipliers = filteredMultipliers;

      if (typeof estimatedValue === 'number' && filteredMultipliers.length > 0) {
        if (estimatedValue > 0) {
          collectionCardToSave.stats.predictedValue = estimatedValue;
          collectionCardToSave.stats.currentValue = collectionCardToSave.stats.predictedValue || 0;
        } else {
          collectionCardToSave.stats.predictedValue = null;
          collectionCardToSave.stats.currentValue = collectionCardToSave.estimatedCurrentValue || 0;
        }

        collectionCardToSave.stats.guideValue = null;
        collectionCardToSave.currentValue = collectionCardToSave.stats.currentValue;
      } else {
        failedToSetupValues = true;
      }
    } else if (collectionCardToSave.useSalesForValue && collectionCardToSave.useLastSoldValue) {
      if (collectionCardToSave.stats && collectionCardToSave.stats.newestSale) {
        collectionCardToSave.stats.guideValue = null;
        collectionCardToSave.stats.predictedValue = null;
        collectionCardToSave.stats.currentValue = collectionCardToSave.stats.newestSale.price;
        collectionCardToSave.currentValue = collectionCardToSave.stats.newestSale.price;
      }
    } else if (collectionCardToSave.useGradedCardAutomatic && collectionCardToSave.profileId && collectionCardToSave.grader && collectionCardToSave.grade) {
      try {
        const request = {
          profileId: collectionCardToSave.profileId,
          grader: collectionCardToSave.grader,
          grade: collectionCardToSave.grade,
          description: collectionCardToSave.player || '',
          qualifierType: collectionCardToSave.qualifierType || null,
          autographGrade: collectionCardToSave.autographGrade || null,
        };

        const { data: estimateResponse } = await firebase.functions().httpsCallable('httpcardestimate')(request);

        if (estimateResponse && estimateResponse.estimatedValue && estimateResponse.indexId) {
          collectionCardToSave.stats.guideValue = estimateResponse.estimatedValue;
          collectionCardToSave.stats.predictedValue = null;
          collectionCardToSave.stats.currentValue = estimateResponse.estimatedValue;
          collectionCardToSave.currentValue = estimateResponse.estimatedValue;

          if (collectionCardToSave.useLastSoldValue && estimateResponse.lastSalePrice && estimateResponse.lastSalePrice > 0) {
            collectionCardToSave.stats.guideValue = null;
            collectionCardToSave.stats.predictedValue = null;
            collectionCardToSave.stats.currentValue = estimateResponse.lastSalePrice;
            collectionCardToSave.currentValue = estimateResponse.lastSalePrice;
          }

          if (estimateResponse.isPlayerIndex) {
            collectionCardToSave.playerIndexId = estimateResponse.indexId;
          } else {
            collectionCardToSave.otherIndexId = estimateResponse.indexId;
          }
        } else {
          failedToSetupValues = true;
        }
      } catch (e) {
        failedToSetupValues = true;
      }

      if (failedToSetupValues && collectionCardToSave.player) {
        try {
          const { data: indexResponse } = await firebase.functions().httpsCallable('httpIndexFromDescription')({ description: collectionCardToSave.player });

          if (indexResponse && indexResponse.id) {
            const { value } = getGuideValueFromCollectionCard(indexResponse, collectionCardToSave);

            if (typeof value === 'number') {
              collectionCardToSave.stats.guideValue = value;
              collectionCardToSave.stats.predictedValue = null;
              collectionCardToSave.stats.currentValue = value;
              collectionCardToSave.currentValue = value;
              collectionCardToSave.useGradedCardAutomatic = false;

              if (indexResponse.isPlayerIndex) {
                collectionCardToSave.playerIndexId = indexResponse.id;
              } else {
                collectionCardToSave.otherIndexId = indexResponse.id;
                collectionCardToSave.playerIndexDate = collectionCardToSave.datePurchased;
                collectionCardToSave.playerIndexPrice = collectionCardToSave.investment;
              }

              failedToSetupValues = false;
            }
          }
        } catch (e) {
          // swallow
        }
      }
    } else if (collectionCardToSave.playerIndexId || collectionCardToSave.otherIndexId) {
      const index = await indexService.getIndex(collectionCardToSave.playerIndexId ? 'players' : 'indexes', collectionCardToSave.playerIndexId || collectionCardToSave.otherIndexId);

      if (index) {
        const { value } = getGuideValueFromCollectionCard(index, collectionCardToSave);

        if (typeof value === 'number') {
          collectionCardToSave.stats.guideValue = value;
          collectionCardToSave.stats.predictedValue = null;
          collectionCardToSave.stats.currentValue = value;
          collectionCardToSave.currentValue = value;
        } else {
          failedToSetupValues = true;
        }
      } else {
        failedToSetupValues = true;
      }
    } else {
      failedToSetupValues = true;
    }
  }

  if (failedToSetupValues) {
    collectionCardToSave.stats = {};
    collectionCardToSave.stats.predictedValue = null;
    collectionCardToSave.stats.guideValue = null;
    collectionCardToSave.stats.currentValue = collectionCardToSave.estimatedCurrentValue || 0;
    collectionCardToSave.currentValue = collectionCardToSave.estimatedCurrentValue || 0;
  }

  return { ...collectionCardToSave, multipliers };
}

export function getCollectionStats(collection, zoom = null) {
  if (!collection) {
    return {};
  }

  const todayKey = moment().utcOffset(-7).format('MM/DD/YYYY');

  let roi = 0;
  let profit = 0;

  if (collection.totalValue && collection.totalInvestment) {
    roi = (collection.totalValue - collection.totalInvestment) / collection.totalInvestment;
    profit = collection.totalValue - collection.totalInvestment;
  }

  let soldRoi = 0;
  let soldProfit = 0;

  if (collection.totalSoldValue && collection.totalSoldInvestment) {
    soldRoi = (collection.totalSoldValue - collection.totalSoldInvestment) / collection.totalSoldInvestment;
    soldProfit = collection.totalSoldValue - collection.totalSoldInvestment;
  }

  let valueGraph = {};
  let dollarChange = 0;
  let percentChange = 0;
  let startValue = 0;

  if (collection.valueGraph) {
    const { valueGraph: collectionValueGraph } = collection;

    if (collection.totalValue) {
      collectionValueGraph[todayKey] = collection.totalValue;
    }

    valueGraph = { label: 'Value', data: collectionValueGraph };

    const timelineData = [];
    const sortedDates = getSortedDates(collectionValueGraph, zoom);

    sortedDates.forEach((d) => {
      timelineData.push({ date: d, price: collectionValueGraph[d] });
    });

    startValue = timelineData.length > 0 ? timelineData[0].price : 0;

    dollarChange = timelineData.length < 2 ? 0 : collection.totalValue - startValue;
    percentChange = timelineData.length < 2 || startValue === 0 ? 0 : (collection.totalValue - startValue) / startValue;
  }

  let investmentGraph = {};

  if (collection.investmentGraph) {
    const { investmentGraph: collectionInvestmentGraph } = collection;

    if (collection.totalInvestment) {
      collectionInvestmentGraph[todayKey] = collection.totalInvestment;
    }

    investmentGraph = { label: 'Investment', data: collectionInvestmentGraph };
  }

  let dailyPercentChange = 0;
  let dailyDollarChange = 0;

  const hasDailyChange = collection.totalValue
    && collection.lastTotalValue
    && collection.valueMovement
    && collection.lastTotalValue - collection.valueMovement !== 0;

  if (hasDailyChange) {
    dailyDollarChange = collection.valueMovement;
    dailyPercentChange = dailyDollarChange / (collection.lastTotalValue - collection.valueMovement);
  }

  const valueStats = [{
    label: 'Collection Value',
    value: toCurrency(collection.totalValue, { full: true }),
  }, {
    label: 'Total Investment',
    value: toCurrency(collection.totalInvestment, { full: true }),
  }, {
    label: '# of Cards Owned',
    value: toNumber(collection.totalNumCards),
  }, {
    label: 'Potential Profit',
    value: `${profit > 0 ? '+' : ''}${toCurrency(profit, { full: true })}`,
    negative: profit < 0,
    positive: profit > 0,
  }, {
    label: 'Potential ROI',
    value: `${roi > 0 ? '+' : ''}${toPercentage(roi)}`,
    negative: roi < 0,
    positive: roi > 0,
  }, {
    label: 'Starting Value',
    value: toCurrency(startValue, { full: true }),
  }, {
    label: 'Rate of Growth',
    value: `${percentChange > 0 ? '+' : ''}${toPercentage(percentChange)}`,
    negative: percentChange < 0,
    positive: percentChange > 0,
  }, {
    label: 'Real Dollar Change',
    value: `${dollarChange > 0 ? '+' : ''}${toCurrency(dollarChange)}`,
    negative: dollarChange < 0,
    positive: dollarChange > 0,
  }];

  let soldStats = [];

  if (collection.totalNumSoldCards > 0) {
    soldStats = [{
      label: 'Total Sold',
      value: toCurrency(collection.totalSoldValue),
    }, {
      label: 'Sold Investment',
      value: toCurrency(collection.totalSoldInvestment),
    }, {
      label: '# of Cards Sold',
      value: toNumber(collection.totalNumSoldCards),
    }, {
      label: 'Sold Profit',
      value: `${soldProfit > 0 ? '+' : ''}${toCurrency(soldProfit)}`,
      negative: soldProfit < 0,
      positive: soldProfit > 0,
    }, {
      label: 'Sold ROI',
      value: `${soldRoi > 0 ? '+' : ''}${toPercentage(soldRoi)}`,
      negative: soldRoi < 0,
      positive: soldRoi > 0,
    }];
  }

  return {
    roi,
    profit,
    soldRoi,
    soldProfit,
    valueGraph,
    investmentGraph,
    dailyDollarChange,
    dailyPercentChange,
    valueStats,
    soldStats,
  };
}

export function combineCollectionData(collections) {
  if (!collections || collections.length === 0) {
    return {};
  }

  const data = {
    ...DEFAULT_COLLECTION_DATA,
    id: 'total',
    lastTotalValue: 0,
    valueMovement: 0,
    name: 'Total',
  };

  collections.forEach((collection) => {
    data.lastTotalValue += collection.lastTotalValue || collection.totalValue || 0;
    data.valueMovement += collection.valueMovement || 0;

    Object.keys(DEFAULT_COLLECTION_DATA).forEach((key) => {
      data[key] += collection[key] || 0;
    });
  });

  return data;
}

export async function getMultiplierResults(collectionCard, cardId, cardValue, saleService) {
  const results = {
    value: 1,
    text: '',
    found: false,
  };

  try {
    let baselinePrice = null;

    if (collectionCard.willManageQuantity && collectionCard.individualCards && collectionCard.individualCards.length > 0) {
      baselinePrice = collectionCard.individualCards[0].investment;
    }

    baselinePrice = baselinePrice || collectionCard.investment;

    let baselineDate = null;

    if (collectionCard.willManageQuantity && collectionCard.individualCards && collectionCard.individualCards.length > 0) {
      baselineDate = collectionCard.individualCards[0].datePurchased;
    }

    baselineDate = baselineDate || collectionCard.datePurchased;

    if (typeof baselinePrice === 'number' && baselineDate) {
      const closestSale = await saleService.findClosestSale(
        cardId,
        baselineDate,
        moment(baselineDate).subtract(30, 'days').toDate(),
        moment(baselineDate).add(30, 'days').toDate(),
      );

      if (closestSale && typeof closestSale.price === 'number' && closestSale.price > 0) {
        results.value = parseFloat((baselinePrice / closestSale.price).toFixed(2));
        results.found = true;
        results.text = `We found a sale close to your date of purchase. It sold for ${toCurrency(closestSale.price)} on ${moment(closestSale.date).format('M/DD/YYYY')}. Below is the estimated ratio between your card and the "comp" card on that date.`;
      } else if (typeof collectionCard.estimatedCurrentValue === 'number' && typeof cardValue === 'number') {
        results.value = parseFloat((collectionCard.estimatedCurrentValue / cardValue).toFixed(2));
        results.text = 'We were not able to find a sale close enough to your purchase date so we went ahead and setup the ratio to match your estimated value.';
      } else {
        results.text = 'We were not able to find a sale close enough to your purchase date. Please provide your own ratio.';
      }
    }
  } catch (e) {
    // swallow
  }

  return results;
}
